import React, { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";

import { useNotification } from "@hooks";
import { useForm, Controller } from "react-hook-form";

import ModalIndex from "@Modals/layout/index";
import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import Button from "@components/tailwindComponents/Button";
import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";

import { formatDateToDateString } from "@helpers/_functions";
import { OnboardingEmployeTask } from "@services/all.services";
import { NotificationUtil } from "@utils/notification.utils";

const SideModal = ({
  tasks,
  openSideModal,
  closeSideModal,
  employeDropdown,
  setSelectedTasks
}) => {
  const { t } = useTranslation("beginEnd.protocol");
  const { showError, showSuccess } = useNotification();

  const { handleSubmit, control, reset } = useForm();

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!openSideModal) {
      reset({
        assignedTo: [],
        predictedDate: null,
        deadlineDate: null
      });
      setIsSaving(false);
    }
  }, [openSideModal]);

  async function handleSubmitSuccess(formValue) {
    try {
      setIsSaving(true);
      let { assignedTo, deadlineDate, predictedDate } = formValue;

      for (let task of tasks) {
        const newTask = await OnboardingEmployeTask.updateTask(task._id, {
          ...task,
          assignedTo: [
            ...new Set(
              assignedTo
                .map((e) => e.id)
                ?.concat(task?.assignedTo.map((e) => e._id))
            )
          ],
          predictedDate: formatDateToDateString(predictedDate),
          deadlineDate: formatDateToDateString(deadlineDate),
          predictedDateTriggerInterval: 0,
          predictedDateTriggerTask: null,
          deadlineDateTriggerInterval: 0,
          deadlineDateTriggerTask: null
        });
        try {
          // send notification
          if (newTask?.assignedTo?.length)
            await NotificationUtil.onboardingEmployeTask(newTask);
        } catch (err) {
          console.log("Error when sending notification");
        }
      }

      await showSuccess("assignation_action_success", {
        object: "aProtocol"
      });
      await closeSideModal();
      setIsSaving(false);
      setSelectedTasks([]);
    } catch (err) {
      console.error(err);
      showError("error");
      setIsSaving(false);
    }
  }

  function handleSubmitError(error) {
    console.log({ error });
    showError("field_obligatory");
  }

  return (
    <ModalIndex
      isOpen={openSideModal}
      onClickClose={closeSideModal}
      title={t("Assigner une tâche")}
      buttonType="none"
    >
      <div className="arh-flex arh-flex-col arh-space-y-3">
        <Controller
          control={control}
          name="assignedTo"
          defaultValue={[]}
          rules={{ required: true }}
          render={({
            field: { onChange, value, name },
            fieldState: { invalid }
          }) => (
            <ListboxEmploye
              label={t("Assigné à")}
              placeholder={t("Employé")}
              name={name}
              optionList={employeDropdown}
              selectedValue={value}
              setSelectedValue={onChange}
              invalid={invalid}
              customClass="arh-ml-[0.925rem]"
              multiple
            />
          )}
        />

        <div className="arh-mb-[0.938rem] arh-flex arh-w-full arh-items-center arh-justify-between">
          <div className="employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-flex-[0.5_0.5_0%]">
            <Controller
              control={control}
              name="predictedDate"
              rules={{ required: true }}
              defaultValue=""
              render={({
                field: { onChange, value },
                fieldState: { invalid }
              }) => (
                <>
                  <label
                    className={`${invalid ? "arh-text-invalidColorTextInput" : "arh-text-black"} arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium`}
                  >
                    {t("Date prévue")}
                  </label>
                  <DefaultDatePicker
                    className="datepickerDropdown GreenThemeSelect"
                    name="date_predicted"
                    placeholder={t("Date prévue")}
                    dateValue={value}
                    setDateValue={onChange}
                    classPosition="calendar_left arh-flex arh-justify-center"
                    invalid={invalid}
                    isInForeground
                    // customClass="arh-bottom-12"
                    marginTop={0}
                  />
                </>
              )}
            />
          </div>

          <div className="employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-flex-[0.5_0.5_0%] last:arh-ml-[1.25rem]">
            <label className="arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium arh-text-black">
              {t("Date limite")}
            </label>
            <Controller
              control={control}
              name="deadlineDate"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <DefaultDatePicker
                  className="datepickerDropdown GreenThemeSelect"
                  name="deadline"
                  placeholder={t("Date limite")}
                  dateValue={value}
                  setDateValue={onChange}
                  classPosition="calendar_right arh-flex arh-justify-center"
                  isInForeground
                  // customClass="arh-bottom-12"
                  marginTop={0}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="arh-mt-10 arh-flex arh-w-full arh-items-center arh-justify-between arh-space-x-4">
        <Button
          variant="outlined"
          onClick={closeSideModal}
        >
          {t("Annuler")}
        </Button>
        <Button
          onClick={handleSubmit(handleSubmitSuccess, handleSubmitError)}
          disabled={isSaving}
        >
          {t("Enregistrer")}
        </Button>
      </div>
    </ModalIndex>
  );
};

export default SideModal;
